import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity, Plus, Search, X, Filter,
  ChevronDown, ChevronUp, Download, Upload, FileText,
  FileSpreadsheet, FileOutput, List, LayoutList, LayoutGrid,
  Shield, Layers, RefreshCcw,
  CheckCircle, Tag, XCircle,
  Palette, Hash, Package, FileJson, Lock
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import { CustomSelect, CustomNumberInput } from '../../shared/components/custom';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { useTranslation } from 'react-i18next';
import {sliceQawaemStats, QAWAEM_KPI_GRID_CLASS, QAWAEM_TABLE_ACTIONS_TH_CLASS, QawaemTableSelectAllHeader, QawaemTableSelectRowCell, createArticleStatusChangeHandler, mapStatusOptionsToColumnFilter, useQawaemColumnVisibility, QawaemTableStickyActionsTd, sliceArticlesListItems } from './articlesListPageUtils';
import { useArticlesConfig, useArticlesListViewMode } from './useArticlesConfig';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { AppConfirmDialog } from '../../shared/components/AppConfirmDialog';
import { useCrudDeleteConfirm } from '../../shared/hooks/useCrudDeleteConfirm';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';

const NS = 'modules.article.pages.cycleViePage';

const CycleViePage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const articlesConfig = useArticlesConfig();

  const PHASE_OPTIONS = [
    { value: 'brouillon', label: p('phase_draft') },
    { value: 'actif', label: p('phase_active') },
    { value: 'obsolète', label: p('phase_obsolete') },
    { value: 'archivé', label: p('phase_archived') }
  ];

  const STATUS_OPTIONS = useMemo(
    () => [
      { value: 'actif', label: t('common.active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
      { value: 'inactif', label: t('common.inactive'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    ],
    [t],
  );

  const formStatusOptions = useMemo(
    () => STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label, isDefault: true })),
    [STATUS_OPTIONS],
  );

  const statusColumnFilterOptions = useMemo(
    () => mapStatusOptionsToColumnFilter(STATUS_OPTIONS),
    [STATUS_OPTIONS],
  );

  const getPhaseLabel = (phase) => {
    const match = PHASE_OPTIONS.find(opt => opt.value === phase);
    return match?.label || phase || 'N/A';
  };

  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [viewMode, setViewMode] = useArticlesListViewMode(articlesConfig);

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_statuses');
  const [quickFilter, setQuickFilter] = useState('all');

  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const {
    deleteConfirm,
    closeDeleteConfirm,
    openDeleteOne,
    openBulkDelete,
    takePendingIds,
  } = useCrudDeleteConfirm();
  const entityLabel = p('entity_label', { defaultValue: 'حالة دورة الحياة' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'حالة' });

  const [formTab, setFormTab] = useState('identity');

  const [formData, setFormData] = useState({
    status_code: '',
    label: '',
    phase: '',
    color: '',
    sort_order: '',
    rules_json: '',
    is_system: '',
    product_count: '',
    status: '',
    notes: ''
  });

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    loadStatuses();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadStatuses = async () => {
    try {
      setLoading(true);
      const response = await localApiAdapter.get('/api/articles/cycle-vie');
      const data = response?.data ?? response;
      setStatuses(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error loading lifecycle statuses:', error);
      setStatuses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = useCallback(
    createArticleStatusChangeHandler('/api/articles/cycle-vie', loadStatuses),
    [],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.status_code.trim() || !formData.label.trim()) {
      window.alert(p('code_label_required'));
      return;
    }
    try {
      setLoading(true);
      const payload = {
        ...formData,
        sort_order: formData.sort_order !== '' ? parseInt(formData.sort_order, 10) : 0,
        product_count: formData.product_count !== '' ? parseInt(formData.product_count, 10) : 0,
        is_system: formData.is_system === '1' || formData.is_system === 1 || formData.is_system === true ? 1 : 0,
        status: formData.status || 'actif'
      };
      if (selectedRecord) {
        await localApiAdapter.put(`/api/articles/cycle-vie/${selectedRecord.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/api/articles/cycle-vie', payload);
        notifyCreated(showNotify, entityLabel);
      }
      await loadStatuses();
      resetForm();
    } catch (error) {
      console.error('Error saving lifecycle status:', error);
      notifyError(showNotify, error?.response?.data?.error || p('save_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setFormData({
      status_code: record.status_code ?? '',
      label: record.label ?? '',
      phase: record.phase ?? '',
      color: record.color ?? '',
      sort_order: record.sort_order != null ? String(record.sort_order) : '',
      rules_json: record.rules_json ?? '',
      is_system: record.is_system != null ? String(record.is_system) : '',
      product_count: record.product_count != null ? String(record.product_count) : '',
      status: record.status ?? '',
      notes: record.notes ?? ''
    });
    setFormTab('identity');
    setShowModal(true);
  };

  const handleDelete = (id) => {
    const record = statuses.find((s) => s.id === id);
    if (record?.is_system === 1) {
      window.alert(p('cannot_delete_system'));
      return;
    }
    openDeleteOne(id);
  };

  const handleBulkDelete = () => {
    const deletable = selectedRecords.filter((id) => {
      const record = statuses.find((s) => s.id === id);
      return record && record.is_system !== 1;
    });
    if (deletable.length === 0) {
      window.alert(p('no_deletable_selected'));
      return;
    }
    openBulkDelete(deletable);
  };

  const executeDeleteConfirmed = async () => {
    const ids = takePendingIds();
    if (!ids.length) return;
    try {
      if (ids.length === 1) {
        setLoading(true);
        await localApiAdapter.delete(`/api/articles/cycle-vie/${ids[0]}`);
        notifyDeleted(showNotify, entityLabel);
        await loadStatuses();
      } else {
        await Promise.all(ids.map((id) => localApiAdapter.delete(`/api/articles/cycle-vie/${id}`)));
        notifyBulkDeleted(showNotify, ids.length, entityLabelPlural);
        setSelectedRecords([]);
        loadStatuses();
      }
    } catch (error) {
      console.error('Error deleting lifecycle status:', error);
      notifyError(showNotify, error?.response?.data?.error || p('delete_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleView = (record) => {
    setSelectedRecord(record);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({
      status_code: '',
      label: '',
      phase: '',
      color: '',
      sort_order: '',
      rules_json: '',
      is_system: '',
      product_count: '',
      status: '',
      notes: ''
    });
    setSelectedRecord(null);
    setFormTab('identity');
    setShowModal(false);
  };

  const formTabs = useMemo(() => ([
    { id: 'identity', label: p('section_identity'), icon: Hash },
    { id: 'rules', label: p('section_rules'), icon: FileJson },
  ]), [p]);

  const handleSelectRecord = (id) => {
    setSelectedRecords(selectedRecords.includes(id)
      ? selectedRecords.filter(rId => rId !== id)
      : [...selectedRecords, id]
    );
  };

  const stats = {
    total: statuses.length,
    actifs: statuses.filter(s => s.status === 'actif').length,
    phasesDistinctes: new Set(statuses.map(s => s.phase).filter(Boolean)).size,
    systeme: statuses.filter(s => s.is_system === 1).length,
    produitsAffectes: statuses.reduce((sum, s) => sum + (parseInt(s.product_count, 10) || 0), 0)
  };

  const lifecycleDistribution = {
    all_statuses: {
      title: p('dist_overview'),
      data: [
        { name: p('dist_actives'), count: stats.actifs, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { name: p('dist_system'), count: stats.systeme, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { name: p('dist_phases'), count: stats.phasesDistinctes, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { name: p('dist_products'), count: stats.produitsAffectes, color: 'bg-gradient-to-r from-amber-500 to-amber-600' }
      ]
    },
    par_phase: {
      title: p('dist_by_phase'),
      data: PHASE_OPTIONS.map((opt, i) => ({
        name: opt.label,
        count: statuses.filter(s => s.phase === opt.value).length,
        color: [
          'bg-gradient-to-r from-slate-500 to-slate-600',
          'bg-gradient-to-r from-green-500 to-green-600',
          'bg-gradient-to-r from-amber-500 to-amber-600',
          'bg-gradient-to-r from-gray-500 to-gray-600'
        ][i]
      }))
    },
    par_statut: {
      title: p('dist_by_status'),
      data: [
        { name: p('dist_actives'), count: stats.actifs, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { name: t('common.inactive'), count: statuses.filter(s => s.status === 'inactif').length, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    }
  };

  const distributionInfo = lifecycleDistribution[mainViewMode] || lifecycleDistribution.all_statuses;

  const mainTabs = [
    { id: 'all_statuses', label: t('common.general_view'), icon: LayoutGrid },
    { id: 'par_phase', label: p('tab_by_phase'), icon: Activity },
    { id: 'par_statut', label: t('common.by_status'), icon: Shield }
  ];

  const quickFilterButtons = [
    { id: 'all', label: p('quick_all_phases'), icon: Layers },
    { id: 'brouillon', label: p('phase_draft'), icon: FileText },
    { id: 'actif', label: p('phase_active'), icon: CheckCircle },
    { id: 'obsolète', label: p('phase_obsolete'), icon: Tag },
    { id: 'archivé', label: p('phase_archived'), icon: Lock }
  ];

  const baseFilteredStatuses = useMemo(
    () =>
      statuses.filter((record) => {
        const matchesFilter = quickFilter === 'all' || record.phase === quickFilter;
        const matchesSearch =
          !searchTerm ||
          record.status_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.phase?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesFilter && matchesSearch;
      }),
    [statuses, quickFilter, searchTerm],
  );

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('status_code', (r) => r.status_code || ''),
      textColumnRule('label', (r) => r.label || ''),
      textColumnRule('phase', (r) => getPhaseLabel(r.phase)),
      textColumnRule('sort_order', (r) => String(parseInt(r.sort_order, 10) || 0)),
      textColumnRule('product_count', (r) => String(parseInt(r.product_count, 10) || 0)),
      textColumnRule('color', (r) => r.color || ''),
      textColumnRule('is_system', (r) => (r.is_system ? p('yes') : p('no'))),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [t, p],
  );

  const filteredStatuses = useMemo(
    () => applyColumnFilters(baseFilteredStatuses, colFilters, columnFilterRules),
    [baseFilteredStatuses, colFilters, columnFilterRules],
  );

  const filteredStatusIds = useMemo(() => filteredStatuses.map((s) => s.id), [filteredStatuses]);
  const displayedStatuses = useMemo(
    () => sliceArticlesListItems(filteredStatuses, articlesConfig.itemsPerPage),
    [filteredStatuses, articlesConfig.itemsPerPage],
  );
  const allRowsSelected = filteredStatusIds.length > 0 && filteredStatusIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;
  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) setSelectedRecords([]);
    else setSelectedRecords(filteredStatusIds);
  };

  const getPhaseBadge = (phase) => {
    const styles = {
      brouillon: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
      actif: 'bg-green-500/20 text-green-400 border-green-500/30',
      'obsolète': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      'archivé': 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${styles[phase] || 'bg-white/10 text-white/70 border-white/20'}`}>
        {getPhaseLabel(phase)}
      </span>
    );
  };

  const { columnVisibility, handleToggleColumn, showCol, visibleDataColCount } = useQawaemColumnVisibility([
    'status_code', 'label', 'phase', 'color', 'sort_order', 'is_system', 'product_count', 'status',
  ]);

  const qawaemColumns = useMemo(
    () => [
      { key: 'status_code', label: p('col_code'), checked: columnVisibility.status_code !== false, required: true },
      { key: 'label', label: p('col_label'), checked: columnVisibility.label !== false, required: true },
      { key: 'phase', label: p('col_phase'), checked: columnVisibility.phase !== false },
      { key: 'color', label: p('col_color'), checked: columnVisibility.color !== false },
      { key: 'sort_order', label: p('col_order'), checked: columnVisibility.sort_order !== false },
      { key: 'is_system', label: p('col_system'), checked: columnVisibility.is_system !== false },
      { key: 'product_count', label: p('col_products'), checked: columnVisibility.product_count !== false },
      { key: 'status', label: t('common.status'), checked: columnVisibility.status !== false },
      { key: 'actions', label: t('common.actions'), checked: true, required: true },
    ],
    [t, p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked).length;
  const tableColSpan = 1 + visibleDataColCount + 1;

  return (
    <div className="space-y-6 employees-container">
      {/* Header */}
      <div className="flex justify-between items-start list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Activity className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadStatuses} className="refresh-button" title={t('common.refresh')}>
              <RefreshCcw size={20} />
            </button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{t('common.advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={() => { resetForm(); setFormTab('identity'); setShowModal(true); }}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_status')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={QAWAEM_KPI_GRID_CLASS}>
        {sliceQawaemStats([
          { label: t('common.total'), value: stats.total, icon: Activity, color: 'from-blue-500 to-blue-600' },
          { label: p('dist_actives'), value: stats.actifs, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { label: p('stat_distinct_phases'), value: stats.phasesDistinctes, icon: Layers, color: 'from-purple-500 to-purple-600' },
          { label: p('stat_system'), value: stats.systeme, icon: Shield, color: 'from-amber-500 to-amber-600' },
          { label: p('stat_affected_products'), value: stats.produitsAffectes, icon: Package, color: 'from-cyan-500 to-cyan-600' }
        ], 4).map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative group"
          >
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.label}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`} style={{ pointerEvents: 'none' }} />
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 space-y-6 overflow-hidden"
          >
            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('main_view')}</h3>
              <div className="flex flex-wrap gap-2">
                {mainTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setMainViewMode(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                      mainViewMode === tab.id
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('quick_filters')}</h3>
              <div className="flex flex-wrap gap-2">
                {quickFilterButtons.map(btn => (
                  <button
                    key={btn.id}
                    onClick={() => setQuickFilter(btn.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      quickFilter === btn.id
                        ? 'bg-purple-500/30 text-purple-200 border-2 border-purple-400/50'
                        : 'bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                  >
                    <btn.icon className="h-4 w-4" />
                    {btn.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{distributionInfo.title}</h3>
              <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
                {distributionInfo.data.map((item, index) => {
                  const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                  const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
                  return (
                    <div key={index} className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="flex justify-between items-center mb-2">
                        <span className="block w-full text-white/70 text-sm font-medium text-right">{item.name}</span>
                        <span className="text-white font-semibold">{item.count} ({percentage}%)</span>
                      </div>
                      <div className="h-2.5 bg-white/5 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ duration: 0.6, delay: index * 0.1 }}
                          className={`h-full ${item.color} rounded-full`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="glass-panel glass-table-shell overflow-visible">

        

        

        

        {viewMode === 'table' && !loading && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onChange={handleSelectAll} />
                  {showCol('status_code') ? (
                    <TableColumnHeaderFilter columnKey="status_code" label={p('col_code')} value={colFilters.status_code ?? ''} onChange={(v) => setColFilter('status_code', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} />
                  ) : null}
                  {showCol('label') ? (
                    <TableColumnHeaderFilter columnKey="label" label={p('col_label')} value={colFilters.label ?? ''} onChange={(v) => setColFilter('label', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} />
                  ) : null}
                  {showCol('phase') ? (
                    <TableColumnHeaderFilter columnKey="phase" label={p('col_phase')} value={colFilters.phase ?? ''} onChange={(v) => setColFilter('phase', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} />
                  ) : null}
                  {showCol('color') ? <TableColumnHeaderFilter columnKey="color" label={p('col_color')} value={colFilters.color ?? ''} onChange={(v) => setColFilter('color', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('sort_order') ? (
                    <TableColumnHeaderFilter columnKey="sort_order" label={p('col_order')} value={colFilters.sort_order ?? ''} onChange={(v) => setColFilter('sort_order', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} />
                  ) : null}
                  {showCol('is_system') ? <TableColumnHeaderFilter columnKey="is_system" label={p('col_system')} value={colFilters.is_system ?? ''} onChange={(v) => setColFilter('is_system', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" /> : null}
                  {showCol('product_count') ? (
                    <TableColumnHeaderFilter columnKey="product_count" label={p('col_products')} value={colFilters.product_count ?? ''} onChange={(v) => setColFilter('product_count', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} />
                  ) : null}
                  {showCol('status') ? <TableColumnHeaderFilter columnKey="status" label={t('common.status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" /> : null}
                  <QawaemTableHeaderActionsCell label={t('common.actions')} columns={qawaemColumns} onToggle={handleToggleColumn} visibleCount={qawaemVisibleColumnCount} totalCount={qawaemColumns.length} thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS} />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedStatuses.length > 0 ? displayedStatuses.map((record, index) => (
                  <tr
                    key={record.id}
                    className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}
                  >
                      <QawaemTableSelectRowCell checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                    {showCol('status_code') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold font-mono">{record.status_code}</span>
                          <span className="text-white/50 text-xs mt-0.5">{record.id}</span>
                        </div>
                      </td>
                    ) : null}
                    {showCol('label') ? (
                      <td className="px-6 py-4 text-white font-semibold">{record.label}</td>
                    ) : null}
                    {showCol('phase') ? (
                      <td className="px-6 py-4">{getPhaseBadge(record.phase)}</td>
                    ) : null}
                    {showCol('color') ? (
                      <td className="px-6 py-4">
                        {record.color ? (
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-lg border border-white/20" style={{ backgroundColor: record.color }} />
                            <span className="text-white/60 text-xs font-mono">{record.color}</span>
                          </div>
                        ) : '—'}
                      </td>
                    ) : null}
                    {showCol('sort_order') ? (
                      <td className="px-6 py-4 text-white">{record.sort_order ?? 0}</td>
                    ) : null}
                    {showCol('is_system') ? (
                      <td className="px-6 py-4 text-center">
                        {record.is_system === 1 ? (
                          <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 flex items-center gap-1 justify-center w-fit mx-auto">
                            <Shield size={14} /> {p('system_badge')}
                          </span>
                        ) : (
                          <span className="text-white/40 text-xs">—</span>
                        )}
                      </td>
                    ) : null}
                    {showCol('product_count') ? (
                      <td className="px-6 py-4 text-white">{record.product_count ?? 0}</td>
                    ) : null}
                    {showCol('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={record.status}
                            recordId={record.id}
                            options={STATUS_OPTIONS}
                            onChange={handleStatusChange}
                            title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                          />
                        </div>
                      </td>
                    ) : null}
                    <QawaemTableStickyActionsTd>
                      <QawaemErpRowActions
                        onView={() => handleView(record)}
                        onEdit={() => handleEdit(record)}
                        onDelete={record.is_system === 1 ? undefined : () => handleDelete(record.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={record.is_system === 1 ? p('system_protected') : t('common.delete')}
                      />
                    </QawaemTableStickyActionsTd>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Activity size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm text-white/40 mt-2">{p('empty_hint')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && !loading && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedStatuses.length > 0 ? displayedStatuses.map((record, index) => (
              <motion.div
                key={record.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-3 mb-4">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <Activity size={20} className="text-blue-400" />
                        {record.label}
                      </h3>
                      {getPhaseBadge(record.phase)}
                      {record.is_system === 1 && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30 flex items-center gap-1">
                          <Shield size={12} /> {p('system_badge')}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-white/70">
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('card_identity')}</h5>
                        <div className="space-y-1">
                          <div><span className="font-medium">{p('label_code')}:</span> {record.status_code}</div>
                          <div><span className="font-medium">{p('label_label')}:</span> {record.label}</div>
                          <div><span className="font-medium">{p('label_phase')}:</span> {getPhaseLabel(record.phase)}</div>
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('card_appearance')}</h5>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Palette size={14} className="text-purple-400" />
                            {record.color ? (
                              <span className="flex items-center gap-2">
                                <span className="w-4 h-4 rounded border border-white/20" style={{ backgroundColor: record.color }} />
                                {record.color}
                              </span>
                            ) : 'N/A'}
                          </div>
                          <div><span className="font-medium">{p('col_order')}:</span> {record.sort_order ?? 0}</div>
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('card_config')}</h5>
                        <div className="space-y-1">
                          <div><span className="font-medium">{p('label_status')}:</span> {record.status === 'actif' ? t('common.active') : t('common.inactive')}</div>
                          <div><span className="font-medium">{p('label_products')}:</span> {record.product_count ?? 0}</div>
                          {record.rules_json && (
                            <div className="pt-1 border-t border-white/10">
                              <FileJson size={14} className="inline text-amber-400 mr-1" />
                              {p('rules_defined')}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('card_notes')}</h5>
                        <p className="text-white/60 text-xs">{record.notes || p('no_notes')}</p>
                      </div>
                    </div>
                  </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleView(record)}
                      onEdit={() => handleEdit(record)}
                      onDelete={record.is_system === 1 ? undefined : () => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={record.is_system === 1 ? p('system_protected') : t('common.delete')}
                    />
                </div>
              </motion.div>
            )) : (
              <div className="col-span-full text-center py-12">
                <Activity size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Activity className="h-6 w-6 text-blue-400" />
                {selectedRecord ? p('modal_edit') : p('modal_new')}
              </h2>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  form="cycle-vie-form"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {selectedRecord ? p('save') : t('common.create')}
                </button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="shrink-0 px-6 pt-3 border-b border-white/10">
              <div className="flex flex-wrap gap-1" role="tablist" aria-label={selectedRecord ? p('modal_edit') : p('modal_new')}>
                {formTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = formTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setFormTab(tab.id)}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${
                        active
                          ? 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px'
                          : 'text-white/55 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <TabIcon size={16} className={active ? (tab.id === 'rules' ? 'text-amber-400' : 'text-emerald-400') : ''} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="cycle-vie-form" onSubmit={handleSubmit}>
                {formTab === 'identity' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status_code')}</label>
                      <input type="text" required value={formData.status_code} onChange={(e) => setFormData({ ...formData, status_code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_status_code')} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_label_required')} /></label>
                      <input type="text" required value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_label')} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_phase')}</label>
                      <CustomSelect value={formData.phase} onChange={(val) => setFormData({ ...formData, phase: val })} options={PHASE_OPTIONS.map(opt => ({ ...opt, isDefault: true }))} placeholder={p('placeholder_select_phase')} searchable />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_color')}</label>
                      <input type="text" value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_color')} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_sort_order')}</label>
                      <CustomNumberInput step={1} value={formData.sort_order} onChange={(v) => setFormData({ ...formData, sort_order: v })} placeholder="0" />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('section_notes')}</label>
                      <input type="text" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_notes')} />
                    </div>
                  </div>
                )}

                {formTab === 'rules' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_rules_json')}</label>
                      <input type="text" value={formData.rules_json} onChange={(e) => setFormData({ ...formData, rules_json: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_rules')} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_record_status')}</label>
                      <CustomSelect value={formData.status} onChange={(val) => setFormData({ ...formData, status: val })} options={formStatusOptions} placeholder={t('common.status')} searchable />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_affected_products')}</label>
                      <CustomNumberInput step={1} value={formData.product_count} onChange={(v) => setFormData({ ...formData, product_count: v })} placeholder="0" />
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* View Modal */}
      {showViewModal && selectedRecord && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Activity className="h-6 w-6 text-blue-400" />
                {p('view_details', { name: selectedRecord.label })}
              </h2>
              <button onClick={() => setShowViewModal(false)} className="text-white/60 hover:text-white transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Hash size={18} className="text-emerald-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_identity')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><p className="text-white/60 text-xs uppercase mb-1">{p('label_code')}</p><p className="text-white font-semibold font-mono">{selectedRecord.status_code}</p></div>
                  <div><p className="text-white/60 text-xs uppercase mb-1">{p('label_label')}</p><p className="text-white font-semibold">{selectedRecord.label}</p></div>
                  <div><p className="text-white/60 text-xs uppercase mb-1">{p('label_phase')}</p>{getPhaseBadge(selectedRecord.phase)}</div>
                  <div><p className="text-white/60 text-xs uppercase mb-1">{p('label_status')}</p><p className="text-white font-semibold">{selectedRecord.status === 'actif' ? t('common.active') : t('common.inactive')}</p></div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <Palette size={18} className="text-purple-400" />
                    <h3 className="text-lg font-semibold text-white">{p('view_appearance_order')}</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      {selectedRecord.color && <span className="w-8 h-8 rounded-lg border border-white/20" style={{ backgroundColor: selectedRecord.color }} />}
                      <span className="text-white font-mono">{selectedRecord.color || 'N/A'}</span>
                    </div>
                    <div><span className="text-white/60 text-sm">{p('col_order')}:</span> <span className="text-white font-semibold ml-2">{selectedRecord.sort_order ?? 0}</span></div>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <Package size={18} className="text-cyan-400" />
                    <h3 className="text-lg font-semibold text-white">{p('view_impact_system')}</h3>
                  </div>
                  <div className="space-y-3">
                    <div><span className="text-white/60 text-sm">{p('label_affected_products')}:</span> <span className="text-white font-semibold ml-2">{selectedRecord.product_count ?? 0}</span></div>
                    <div>
                      {selectedRecord.is_system === 1 ? (
                        <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 inline-flex items-center gap-1">
                          <Shield size={14} /> {p('system_protected_full')}
                        </span>
                      ) : (
                        <span className="text-white/60 text-sm">{p('custom_status')}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {(selectedRecord.rules_json || selectedRecord.notes) && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <FileJson size={18} className="text-amber-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_rules_notes')}</h3>
                  </div>
                  {selectedRecord.rules_json && (
                    <pre className="text-white/70 text-xs font-mono bg-black/20 rounded-lg p-3 mb-3 overflow-x-auto">{selectedRecord.rules_json}</pre>
                  )}
                  {selectedRecord.notes && <p className="text-white/70 text-sm italic">{selectedRecord.notes}</p>}
                </div>
              )}
            </div>

            <button onClick={() => setShowViewModal(false)} className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10">
              {t('common.close')}
            </button>
          </motion.div>
        </div>,
        document.body
      )}
            <AppConfirmDialog
        open={deleteConfirm.open}
        title={t('common.delete', 'حذف')}
        message={
          deleteConfirm.ids.length > 1
            ? p('bulk_delete_confirm', { count: deleteConfirm.ids.length })
            : p('delete_confirm')
        }
        confirmLabel={t('common.delete', 'حذف')}
        cancelLabel={t('common.cancel', 'إلغاء')}
        danger
        onCancel={closeDeleteConfirm}
        onConfirm={executeDeleteConfirmed}
      />
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default CycleViePage;
